/**
 * Zoomable — обёртка увеличения для просмотрщиков медиа (постановка Антона
 * 2026-10-05 10:39: «при просмотре картинок и видео должно работать
 * УВЕЛИЧЕНИЕ»). Один механизм на все пути просмотра: карусель MediaViewer,
 * лайтбокс аватара и лайтбокс медиа профиля — второго зума в проекте нет.
 *
 * ЖЕСТЫ (оба ввода — десктоп/Mac мышью и Android/планшет пальцами):
 *  - щипок двумя пальцами: масштаб к точке между пальцами;
 *  - колесо мыши: масштаб к позиции курсора;
 *  - двойной тап / двойной клик: увеличить к точке (2.5x), повторный —
 *    вернуть вписанный размер;
 *  - в увеличенном виде: перетаскивание пальцем или зажатой кнопкой мыши
 *    двигает картинку; края упираются — в пустоту не уезжают;
 *  - пределы масштаба: 1x (вписано в экран) … 5x.
 *
 * КАК РАЗВЕДЕНЫ ЖЕСТЫ (главное место, где такие правки ломаются):
 *  - События — Pointer Events с setPointerCapture + touch-action:none.
 *    touch-action:none вешается на КОНТЕНТ (картинку), а НЕ на всю обёртку:
 *    иначе обёртка перекрывала бы кнопки лайтбокса, и щипок, начатый пальцами
 *    над крестиком/стрелкой, рисковал быть съеденным зумом вместо нажатия.
 *    Так браузер не делает свой скролл/зум по жестам над картинкой:
 *    двухпальцевый щипок и протяжка целиком наши. Android WebView (Chromium)
 *    Pointer Events поддерживает с 2015 года — пограничных API здесь нет.
 *    Стрелки/крестик/шапка живут СНАРУЖИ зумабельной области (сцена — только
 *    центральная часть), их нажатия вообще не проходят через эти обработчики.
 *  - Зум vs листание: листания свайпом в просмотрщике НЕТ ни в одном из
 *    трёх путей (карусель листается стрелками и клавишами ← →), поэтому
 *    однопальцевая протяжка отдана панорамированию без разборок. Стрелки
 *    лежат ПОВЕРХ сцены и получают pointerdown раньше — зумабельная обёртка
 *    их жесты не перехватывает.
 *  - Зум vs закрытие: фон закрывается по onClick (target===currentTarget).
 *    После панорамирования/щипка браузер может бросить «левый» click на
 *    обёртке — гасим его флагом suppressClick в capture-фазе, пока он не
 *    дошёл до onClick фона. Одиночный чистый тап/клик по фону по-прежнему
 *    закрывает; тап по самой картинке — нет.
 *  - Двойной тап считаем сами (pointerup дважды в радиусе 24px за 300мс),
 *    а не через dblclick: на тачах dblclick приходит не везде.
 *
 * ВНУТРИ: scale переводится в пиксели содержимого из naturalWidth/Height
 * (getBoundingClientRect уже отмасштабирован и дал бы runaway-рост масштаба).
 * Панорамирование клампится к краям: увеличенная картинка не уходит за экран,
 * вписанная стоит по центру.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";

/** Минимальный и максимальный масштаб. Верхний предел 5x — внутри просят 4-6x. */
const MIN_SCALE = 1;
const MAX_SCALE = 5;
/** Масштаб по двойному тапу/клику. */
const DOUBLE_TAP_SCALE = 2.5;
/** Окно двойного тапа: мс между тапами и пиксели радиуса. */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_RADIUS = 24;
/** Сколько миллисекунд после жеста гасить «левый» click, чтобы не закрыть лайтбокс. */
const CLICK_SUPPRESS_MS = 350;

type Pt = { x: number; y: number };

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
function mid(a: Pt, b: Pt): Pt {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
/** Кламп туда и обратно. */
function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export function Zoomable({
  children,
  onIsZoomedChange,
}: {
  children: ReactNode;
  /** Уведомление о смене режима (scale>1) — родитель может прятать стрелки. */
  onIsZoomedChange?: (zoomed: boolean) => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /** Указатели жеста: pointerId → позиция. Не state — частые обновления. */
  const pointersRef = useRef<Map<number, Pt>>(new Map());
  /** Режим жеста: однопальцевое панорамирование или двухпальцевый щипок. */
  const pinchRef = useRef<{ startDist: number; startScale: number } | null>(null);
  const panRef = useRef<{ start: Pt; startTx: number; startTy: number } | null>(null);
  /** Было ли реальное движение в жесте (порог 4px) — отличает тап от драга. */
  const movedRef = useRef(false);
  /** Последний тап — для детекции двойного. */
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);
  /** Момент, до которого click по обёртке гасится (после драга/щипка/двойного тапа). */
  const suppressClickUntilRef = useRef(0);
  /** Размеры вписанного содержимого (naturalWidth/Height) для перевода scale в px. */
  const contentSizeRef = useRef<{ w: number; h: number } | null>(null);

  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  /** Пока идёт драг/щипок, transform без transition — иначе лаг за пальцем. */
  const [gesturing, setGesturing] = useState(false);

  const apply = useCallback((s: number, x: number, y: number) => {
    scaleRef.current = s;
    txRef.current = x;
    tyRef.current = y;
    setScale(s);
    setTx(x);
    setTy(y);
  }, []);

  /** Кламп смещения: края увеличенной картинки не уезжают в пустоту. */
  const clampPan = useCallback((s: number, x: number, y: number): { x: number; y: number } => {
    const wrap = wrapRef.current;
    const cs = contentSizeRef.current;
    if (!wrap || !cs || s <= 1) return { x: 0, y: 0 };
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    // Отображаемые размеры содержимого при масштабе s.
    const w = cs.w * s;
    const h = cs.h * s;
    // Содержимое центрировано обёрткой (place-items:center): свободный ход
    // по оси = половина разности размеров. Если содержимое уже обёртки — 0.
    const maxX = Math.max(0, (w - wrapW) / 2);
    const maxY = Math.max(0, (h - wrapH) / 2);
    return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
  }, []);

  /** Масштаб к точке (cx,cy) в координатах обёртки. */
  const zoomTo = useCallback(
    (next: number, cx: number, cy: number) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const s0 = scaleRef.current;
      const s1 = clamp(next, MIN_SCALE, MAX_SCALE);
      const rect = wrap.getBoundingClientRect();
      // Точка относительно центра обёртки (содержимое центрировано).
      const px = cx - (rect.left + rect.width / 2);
      const py = cy - (rect.top + rect.height / 2);
      // Хотим, чтобы точка содержимого под курсором/пальцем осталась на месте:
      // p = (p - t) * s1/s0 + t1  →  t1 = p - (p - t0) * (s1/s0).
      const k = s1 / s0;
      let nx = px - (px - txRef.current) * k;
      let ny = py - (py - tyRef.current) * k;
      if (s1 <= 1) {
        nx = 0;
        ny = 0;
      } else {
        const c = clampPan(s1, nx, ny);
        nx = c.x;
        ny = c.y;
      }
      apply(s1, nx, ny);
    },
    [apply, clampPan],
  );

  const reset = useCallback(() => {
    apply(1, 0, 0);
  }, [apply]);

  // Уведомляем родителя о режиме (он может прятать стрелки листания).
  const isZoomed = scale > 1;
  const notifyRef = useRef(onIsZoomedChange);
  notifyRef.current = onIsZoomedChange;
  useEffect(() => {
    notifyRef.current?.(isZoomed);
  }, [isZoomed]);

  // Колесо мыши: не React onWheel (он passive в React 17+ и preventDefault
  // из него не сработает), а прямой слушатель с passive:false.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault(); // страница под лайтбоксом не крутится
      const factor = Math.exp(-e.deltaY * 0.0015);
      zoomTo(scaleRef.current * factor, e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomTo]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Только левая кнопка мыши / палец / перо.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    wrapRef.current?.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    movedRef.current = false;
    if (pointersRef.current.size === 2) {
      // Второй палец лёг — щипок; однопальцевое панорамирование отменяем.
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { startDist: dist(a, b), startScale: scaleRef.current };
      panRef.current = null;
      // Любой щипок — не тап: и двойной тап, и click по фону отменяем заранее,
      // иначе щипок до scale 1 + поднятие пальцев могло всплыть как «клик по
      // фону» и закрыть лайтбокс.
      lastTapRef.current = null;
      suppressClickUntilRef.current = Date.now() + CLICK_SUPPRESS_MS;
    } else if (pointersRef.current.size === 1) {
      pinchRef.current = null;
      panRef.current = { start: { x: e.clientX, y: e.clientY }, startTx: txRef.current, startTy: tyRef.current };
    }
    setGesturing(true);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pinchRef.current && pointersRef.current.size >= 2) {
      const [a, b] = [...pointersRef.current.values()];
      const d = dist(a, b);
      if (d > 0 && pinchRef.current.startDist > 0) {
        movedRef.current = true;
        const m = mid(a, b);
        zoomTo(pinchRef.current.startScale * (d / pinchRef.current.startDist), m.x, m.y);
      }
      return;
    }
    if (panRef.current && pointersRef.current.size === 1) {
      const dx = e.clientX - panRef.current.start.x;
      const dy = e.clientY - panRef.current.start.y;
      if (!movedRef.current && Math.hypot(dx, dy) < 4) return; // ещё может быть тап
      movedRef.current = true;
      // Панорамирование имеет смысл только в увеличенном виде; вписанная
      // картинка не двигается (и листания свайпом нет — см. шапку).
      if (scaleRef.current > 1) {
        const c = clampPan(scaleRef.current, panRef.current.startTx + dx, panRef.current.startTy + dy);
        apply(scaleRef.current, c.x, c.y);
      }
    }
  };

  const endPointer = (e: ReactPointerEvent<HTMLDivElement>) => {
    const wasPinch = pointersRef.current.size >= 2;
    pointersRef.current.delete(e.pointerId);
    if (wrapRef.current?.hasPointerCapture(e.pointerId)) {
      wrapRef.current.releasePointerCapture(e.pointerId);
    }
    if (pointersRef.current.size === 0) {
      setGesturing(false);
      if (wasPinch || movedRef.current) {
        suppressClickUntilRef.current = Date.now() + CLICK_SUPPRESS_MS;
      } else {
        // Чистый тап без движения — проверяем двойной.
        const now = Date.now();
        const last = lastTapRef.current;
        if (
          last &&
          now - last.t < DOUBLE_TAP_MS &&
          Math.hypot(e.clientX - last.x, e.clientY - last.y) < DOUBLE_TAP_RADIUS
        ) {
          lastTapRef.current = null;
          suppressClickUntilRef.current = now + CLICK_SUPPRESS_MS;
          if (scaleRef.current > 1) {
            reset();
          } else {
            zoomTo(DOUBLE_TAP_SCALE, e.clientX, e.clientY);
          }
        } else {
          lastTapRef.current = { t: now, x: e.clientX, y: e.clientY };
        }
      }
      panRef.current = null;
      pinchRef.current = null;
    } else if (pointersRef.current.size === 1) {
      // Один палец остался после щипка — продолжаем панорамирование с него.
      const [p] = [...pointersRef.current.values()];
      pinchRef.current = null;
      panRef.current = { start: p, startTx: txRef.current, startTy: tyRef.current };
    }
  };

  // Клик после драга/щипка/двойного тапа не должен дойти до onClick фона
  // (закрытие лайтбокса). capture=true — перехватываем ДО обработчиков
  // потомков и фона. Одиночный чистый тап флаг не ставит и закрывает как
  // прежде. e.stopPropagation() хватает: наш onClickCapture — ПЕРВЫЙ на
  // пути события (обёртка — предок фона), и дальше click не идёт никуда.
  const onClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (Date.now() < suppressClickUntilRef.current) {
      suppressClickUntilRef.current = 0;
      e.stopPropagation();
      e.preventDefault();
    }
  };

  // Когда содержимое сменилось (другой слайд) — сбрасываем масштаб и
  // переснимаем natural-размеры. Здесь же: двойной клик мышью на десктопе
  // идёт тем же путём, что и двойной тап (pointer-события), поэтому отдельный
  // onDoubleClick не нужен.
  const contentRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return;
      const measure = () => {
        let w = 0;
        let h = 0;
        if (node instanceof HTMLImageElement) {
          w = node.naturalWidth;
          h = node.naturalHeight;
        } else if (node instanceof HTMLVideoElement) {
          w = node.videoWidth;
          h = node.videoHeight;
        }
        // Ограничиваем natural-размеры вписанными: maxWidth/maxHeight 100%.
        const wrap = wrapRef.current;
        if (wrap && w > 0 && h > 0) {
          const fit = Math.min(wrap.clientWidth / w, wrap.clientHeight / h, 1);
          contentSizeRef.current = { w: w * fit, h: h * fit };
        } else {
          contentSizeRef.current = null;
        }
        reset();
      };
      if (node instanceof HTMLImageElement) {
        if (node.complete && node.naturalWidth > 0) measure();
        else node.addEventListener("load", measure, { once: true });
      } else if (node instanceof HTMLVideoElement) {
        if (node.readyState >= 1) measure();
        else node.addEventListener("loadedmetadata", measure, { once: true });
      } else {
        measure();
      }
    },
    [reset],
  );

  // При ресайзе окна (поворот планшета) пересчитываем кламп.
  useEffect(() => {
    const onResize = () => {
      const c = clampPan(scaleRef.current, txRef.current, tyRef.current);
      apply(scaleRef.current, c.x, c.y);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [apply, clampPan]);

  const style: CSSProperties = {
    transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
    transformOrigin: "center center",
    transition: gesturing ? "none" : "transform 140ms ease-out",
    willChange: "transform",
  };

  return (
    <div
      ref={wrapRef}
      style={{
        width: "100%",
        height: "100%",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        userSelect: "none",
        WebkitUserSelect: "none",
        cursor: isZoomed ? "grab" : "default",
      }}
      data-testid="zoomable"
      data-scale={scale.toFixed(2)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onClickCapture={onClickCapture}
    >
      <div
        ref={contentRef as (node: HTMLDivElement | null) => void}
        style={{
          ...style,
          // touch-action:none — браузер не отбирает жесты над картинкой на
          // свой скролл/зум; обязателен для щипка в Android WebView. На
          // контенте, а не на обёртке: кнопки лайтбокса остаются нажимаемыми.
          touchAction: "none",
        }}
        data-testid="zoomable-content"
      >
        {children}
      </div>
    </div>
  );
}
